/**
 * Image utility component that handles URL resolution for uploaded and external images.
 * Supports both relative URLs (from backend uploads) and absolute URLs (external sources).
 */
import React from 'react';
import { getImageUrl, getUserAvatar, DEFAULT_AVATAR } from '@/utils/helpers';

/**
 * Image component with URL resolution and fallback support
 */
export const Image = ({ src, alt, fallback, className, ...props }) => {
  const resolvedSrc = getImageUrl(src, fallback);
  
  return (
    <img 
      src={resolvedSrc}
      alt={alt || ''}
      className={className}
      onError={(e) => {
        if (fallback && e.target.src !== fallback) {
          e.target.src = fallback;
        }
      }}
      {...props}
    />
  );
};

/**
 * Avatar component for user profile pictures
 */
export const Avatar = ({ src, name, className = "w-10 h-10", ...props }) => {
  // Generate fallback from name if no avatar
  const fallbackUrl = name 
    ? `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=22c55e&color=fff`
    : DEFAULT_AVATAR;
  
  const resolvedSrc = getUserAvatar(src) || fallbackUrl;
  
  return (
    <img 
      src={resolvedSrc}
      alt={name || 'User avatar'}
      className={`rounded-full object-cover ${className}`}
      onError={(e) => {
        if (e.target.src !== fallbackUrl) {
          e.target.src = fallbackUrl;
        }
      }}
      {...props}
    />
  );
};

/**
 * Product/Recipe image component
 */
export const ProductImage = ({ src, alt, className, ...props }) => {
  const fallback = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop';
  
  return (
    <Image 
      src={src}
      alt={alt}
      fallback={fallback}
      className={className}
      {...props}
    />
  );
};

export default Image;
